import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import {
  AIFI_CHART_PRIMARY_COLOR,
  AIFI_CHART_SECONDARY_COLOR,
  AIFI_LEVEL_COLORS,
  AIFI_LEVEL_MIX_TOOLTIP_COUNT_COLOR,
  AIFI_LEVEL_MIX_TOOLTIP_LEVEL_COLOR,
  AIFI_LEVEL_MIX_TOOLTIP_PERCENT_COLOR,
  AIFI_NEGATIVE_COLOR,
  AIFI_POSITIVE_TEXT_COLOR,
  AIFI_TARGET_COLOR,
} from '@/domains/pages/ai-proficiency/constants';
import {
  buildAnalyticsTooltipHtml,
  escapeTooltipHtml,
  PAGE_DATA_NUMBER_INLINE_CSS,
} from '@/domains/pages/shared/chart-tooltip';
import {
  PAGE_TOOLTIP_BODY_CONTAINER_INLINE_CSS,
  PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS,
  PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS,
  PAGE_TOOLTIP_SHELL_INLINE_STYLE,
  PAGE_TOOLTIP_TITLE_INLINE_CSS,
  tooltipSwatchInlineStyle,
} from '@/domains/pages/shared/chart-tooltip.config';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import { formatScore } from '@/utils/format.util';

export type LevelMixTooltipRow = {
  level: AifiLevel;
  percent: number;
  count: number;
};

type OverTimeTooltipParams = {
  header: string;
  orgScore: number | null;
  benchmarkMedian: number;
};

type OrgOnlyOverTimeTooltipParams = {
  header: string;
  orgScore: number | null;
};

type SegmentOverTimeTooltipParams = {
  header: string;
  selectedName: string;
  selectedScore: number | null;
  orgScore: number | null;
};

export function buildOrgOnlyOverTimeTooltipHtml({ header, orgScore }: OrgOnlyOverTimeTooltipParams): string {
  return buildAnalyticsTooltipHtml({
    title: header,
    bodyLines: [{ color: AIFI_CHART_PRIMARY_COLOR, label: 'Org AI proficiency' }],
    footerLines: [{ text: `${formatScore(orgScore, { decimals: 1 })} org AI proficiency` }],
  });
}

export function buildSegmentOverTimeTooltipHtml({
  header,
  selectedName,
  selectedScore,
  orgScore,
}: SegmentOverTimeTooltipParams): string {
  const vsOrg = selectedScore != null && orgScore != null ? selectedScore - orgScore : null;
  const isAhead = vsOrg != null && vsOrg >= 0;
  const deltaColor =
    vsOrg != null ? (isAhead ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR) : undefined;
  const deltaText = vsOrg != null ? `${isAhead ? '+' : ''}${formatScore(vsOrg, { decimals: 1 })} vs. org` : '-';

  return buildAnalyticsTooltipHtml({
    title: header,
    bodyLines: [
      { color: AIFI_CHART_PRIMARY_COLOR, label: selectedName },
      { color: 'rgba(255, 255, 255, 0.45)', label: 'Org' },
    ],
    footerLines: [
      { text: `${formatScore(selectedScore, { decimals: 1 })} ${selectedName}` },
      { text: `${formatScore(orgScore, { decimals: 1 })} org` },
      { text: deltaText, color: deltaColor },
    ],
  });
}

export function buildOverTimeTooltipHtml({
  header,
  orgScore,
  benchmarkMedian,
}: OverTimeTooltipParams): string {
  const delta = orgScore != null ? orgScore - benchmarkMedian : null;
  const isAhead = delta != null && delta >= 0;
  const deltaColor =
    delta != null ? (isAhead ? AIFI_CHART_PRIMARY_COLOR : AIFI_NEGATIVE_COLOR) : undefined;
  const deltaText = delta != null ? `${isAhead ? '+' : ''}${formatScore(delta, { decimals: 1 })} delta` : '-';

  return buildAnalyticsTooltipHtml({
    title: header,
    bodyLines: [
      { color: AIFI_CHART_PRIMARY_COLOR, label: 'Org AI proficiency' },
      { color: AIFI_CHART_SECONDARY_COLOR, label: 'Benchmark median' },
    ],
    footerLines: [
      { text: `${formatScore(orgScore, { decimals: 1 })} org AI proficiency` },
      { text: `${formatScore(benchmarkMedian, { decimals: 1 })} benchmark median` },
      { text: deltaText, color: deltaColor },
    ],
  });
}

export function buildLevelMixTooltipHtml(title: string, rows: LevelMixTooltipRow[]): string {
  const rowHtml = rows
    .map((row) => {
      const color = AIFI_LEVEL_COLORS[row.level];
      return `
        <div style="display:grid;grid-template-columns:8px 1fr auto auto;align-items:center;column-gap:12px;">
          <span style="${tooltipSwatchInlineStyle(color)}"></span>
          <span style="${PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS};color:${AIFI_LEVEL_MIX_TOOLTIP_LEVEL_COLOR};">${row.level}</span>
          <span style="${PAGE_DATA_NUMBER_INLINE_CSS};${PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS};color:${AIFI_LEVEL_MIX_TOOLTIP_PERCENT_COLOR};text-align:right;">${formatScore(row.percent, { decimals: 1 })}%</span>
          <span style="${PAGE_DATA_NUMBER_INLINE_CSS};${PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS};color:${AIFI_LEVEL_MIX_TOOLTIP_COUNT_COLOR};text-align:right;">${formatUserCount(row.count)} users</span>
        </div>
      `;
    })
    .join('');

  return `
    <div style="${PAGE_TOOLTIP_SHELL_INLINE_STYLE}">
      <div style="${PAGE_TOOLTIP_TITLE_INLINE_CSS}">${escapeTooltipHtml(title)}</div>
      <div style="${PAGE_TOOLTIP_BODY_CONTAINER_INLINE_CSS}">${rowHtml}</div>
    </div>
  `;
}

type DimensionRadarTooltipParams = {
  dimensionLabel: string;
  /** What the behavior is, so the reader need not know the dimension by name. */
  dimensionDescription?: string;
  levelLabel: string;
  levelColor: string;
  levelScore: number;
  orgAvg: number;
  target: number;
};

export function buildDimensionRadarTooltipHtml({
  dimensionLabel,
  dimensionDescription,
  levelLabel,
  levelColor,
  levelScore,
  orgAvg,
  target,
}: DimensionRadarTooltipParams): string {
  const vsOrg = levelScore - orgAvg;
  const vsTarget = levelScore - target;
  const vsOrgColor = vsOrg >= 0 ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR;
  const vsOrgText = `${vsOrg >= 0 ? '+' : ''}${formatScore(vsOrg, { decimals: 1 })} vs. org`;
  const vsTargetColor = vsTarget >= 0 ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR;
  const vsTargetText = `${vsTarget >= 0 ? '+' : ''}${formatScore(vsTarget, { decimals: 1 })} vs. target`;

  return buildAnalyticsTooltipHtml({
    title: dimensionLabel,
    subtitle: dimensionDescription,
    bodyLines: [{ color: levelColor, label: levelLabel }],
    footerLines: [
      { text: formatScore(levelScore, { decimals: 1 }) },
      { text: `${formatScore(orgAvg, { decimals: 1 })} org avg` },
      { text: `${formatScore(target, { decimals: 1 })} target`, color: AIFI_TARGET_COLOR },
      { text: vsOrgText, color: vsOrgColor },
      { text: vsTargetText, color: vsTargetColor },
    ],
  });
}
